import { create } from 'zustand'

/** Feature-flag map from billing/status (e.g. `{ csv_export: true }`). */
export type PlanFeatures = Record<string, unknown>

/**
 * Enabled-modules snapshot from billing/status. Carries the optional
 * `disabled_items` nav-key list; remaining keys are module flags. Shape is
 * loose at the boundary — callers read fields defensively.
 */
export interface EnabledModules {
  disabled_items?: string[]
  [key: string]: unknown
}

/** Platform-level kill switches — `false` = globally disabled. */
export type PlatformModules = Record<string, unknown>

/** Phase 28: `{ moduleKey: reasonString }` for modules a super-admin locked off. */
export type PlatformDisabledModules = Record<string, unknown>

/** Sidebar nav-group open/close flags (persists across route changes). */
export type NavGroupsOpen = Record<string, boolean>

export interface SettingsState {
  currency: string
  appName: string
  companyName: string
  logoUrl: string
  timezone: string
  /** Current subscription plan (free, starter, pro, enterprise). */
  planName: string | null | undefined
  planFeatures: PlanFeatures
  /** Subscription status from /billing/plan (active, past_due, free, …). */
  subStatus: string | null
  /** True after settings+billing have been fetched once. */
  settingsLoaded: boolean
  /** null = all enabled (default for existing venues). */
  enabledModules: EnabledModules | null | undefined
  /** Nav keys hidden within enabled groups. */
  disabledItems: string[]
  platformModules: PlatformModules
  platformDisabledModules: PlatformDisabledModules
  /** True when tenant has qa_mode enabled. */
  qaMode: boolean
  gbEnabled: boolean
  gbDefaultStandard: string
  gbCountryCode: string

  setCurrency: (c: string) => void
  setAppName: (n: string) => void
  setCompanyName: (n: string) => void
  setLogoUrl: (v: string) => void
  setTimezone: (tz: string) => void
  setPlanName: (p: string | null | undefined) => void
  setPlanFeatures: (f: unknown) => void
  setSubStatus: (s: string | null | undefined) => void
  hasFeature: (name: string) => boolean
  isInventoryOnly: () => boolean
  setQaMode: (v: unknown) => void
  setGbEnabled: (v: unknown) => void
  setGbDefaultStandard: (s: string) => void
  setGbCountryCode: (c: string) => void
  setEnabledModules: (m: unknown) => void
  isItemEnabled: (navKey: string) => boolean
  setPlatformModules: (m: unknown) => void
  isPlatformModuleEnabled: (key: string) => boolean
  setPlatformDisabledModules: (m: unknown) => void
  isPlatformDisabled: (key: string) => boolean
  getPlatformDisabledReason: (key: string) => string | null
  navGroupsOpen: NavGroupsOpen
  setNavGroupOpen: (group: string, open: boolean) => void
  setSettingsLoaded: (v: boolean) => void
  resetSettings: () => void
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  currency: 'DKK',
  appName: 'Brighella',
  companyName: '',
  logoUrl: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  planName: null,       // current subscription plan (free, starter, pro, enterprise)
  planFeatures: {},     // feature flags from billing/status (e.g. { csv_export: true })
  subStatus: null,      // subscription status (BILL-5: 'past_due' drives the global banner)
  settingsLoaded: false, // true after settings+billing have been fetched once
  enabledModules: null, // null = all enabled (default for existing venues)
  disabledItems: [],    // array of nav keys hidden within enabled groups
  platformModules: {},  // platform-level kill switches — false = globally disabled
  platformDisabledModules: {}, // Phase 28: { moduleKey: reasonString } — modules super-admin has locked off
  qaMode: false,        // true when tenant has qa_mode enabled
  gbEnabled: false,
  gbDefaultStandard: 'basic',
  gbCountryCode: '',
  setCurrency: (c) => set({ currency: c }),
  setAppName: (n) => set({ appName: n }),
  setCompanyName: (n) => set({ companyName: n }),
  setLogoUrl: (v) => set({ logoUrl: v }),
  setTimezone: (tz) => set({ timezone: tz }),
  setPlanName: (p) => set({ planName: p }),
  setPlanFeatures: (f) => set({ planFeatures: (f as PlanFeatures) || {} }),
  setSubStatus: (s) => set({ subStatus: s || null }),
  hasFeature: (name) => !!get().planFeatures[name],
  isInventoryOnly: () => {
    const { planName } = get()
    return ['free', 'starter'].includes(planName ?? '')
  },
  setQaMode: (v) => set({ qaMode: !!v }),
  setGbEnabled: (v) => set({ gbEnabled: !!v }),
  setGbDefaultStandard: (s) => set({ gbDefaultStandard: s }),
  setGbCountryCode: (c) => set({ gbCountryCode: c }),
  setEnabledModules: (m) => {
    const mod = m as EnabledModules | null | undefined
    set({
      enabledModules: mod,
      disabledItems: Array.isArray(mod?.disabled_items) ? mod.disabled_items : [],
    })
  },
  isItemEnabled: (navKey) => {
    const { disabledItems } = get()
    return !disabledItems.includes(navKey)
  },
  setPlatformModules: (m) => set({ platformModules: (m as PlatformModules) || {} }),
  isPlatformModuleEnabled: (key) => get().platformModules[key] !== false,
  setPlatformDisabledModules: (m) => set({
    platformDisabledModules: m && typeof m === 'object' && !Array.isArray(m) ? (m as PlatformDisabledModules) : {},
  }),
  isPlatformDisabled: (key) => Object.prototype.hasOwnProperty.call(get().platformDisabledModules, key),
  getPlatformDisabledReason: (key) => {
    const v = get().platformDisabledModules[key]
    return typeof v === 'string' ? v : null
  },
  // Sidebar nav group open/close state (persists across route changes)
  navGroupsOpen: { inventory: false, productions: false, financial: false },
  setNavGroupOpen: (group, open) => set(s => ({
    navGroupsOpen: { ...s.navGroupsOpen, [group]: open },
  })),
  setSettingsLoaded: (v) => set({ settingsLoaded: v }),
  resetSettings: () => set({
    settingsLoaded: false,
    enabledModules: null,
    disabledItems: [],
    platformModules: {},
    platformDisabledModules: {},
    planName: null,
    planFeatures: {},
    subStatus: null,
    qaMode: false,
    gbEnabled: false,
    gbDefaultStandard: 'basic',
    gbCountryCode: '',
  }),
}))
